import { useState } from 'react';
import type { ReactElement } from 'react';
import type { ReportDetail } from '@tallyframe/core';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { CopyButton } from '../ui/CopyButton';
import { Stack } from '../ui/Stack';
import { Table } from '../ui/Table';
import { CsvUploadDialog, TYPE_LABEL } from './CsvUploadDialog';

/**
 * What a report with no data yet opens on (spec §4 Remix step 5): the columns
 * the data must have, and two ways to supply it. This screen's agent wording
 * names the MCP tools on purpose; they are instructions, not internal state.
 */
export function AwaitingDataPanel(props: {
  detail: ReportDetail;
  upgradeUrl: string | null;
  onUploaded(): void;
}): ReactElement {
  const { detail } = props;
  const [uploading, setUploading] = useState(false);

  return (
    <section data-testid="awaiting-data">
      <Card>
        <Stack gap={4}>
        <h1 className="m-0 p-0 text-2xl font-semibold text-ink font-ui">This report is waiting for data</h1>
        <p className="m-0 p-0 text-md text-muted font-ui">
          {detail.report.definition.title} has its layout but no data yet. Add a CSV with these
          columns, or have your agent send it.
        </p>

        <h2 className="m-0 p-0 text-lg font-semibold text-ink font-ui">Columns</h2>
        <Table
          label="Columns"
          density="compact"
          rows={detail.dataset.columns}
          columns={[
            { id: 'name', label: 'Name', isRowHeader: true, render: (column) => column.label },
            { id: 'type', label: 'Type', render: (column) => TYPE_LABEL[column.type] }
          ]}
        />

        <h2 className="m-0 p-0 text-lg font-semibold text-ink font-ui">Upload a file</h2>
        <div>
          <Button variant="primary" onPress={() => setUploading(true)}>
            Upload a CSV
          </Button>
        </div>

        <h2 className="m-0 p-0 text-lg font-semibold text-ink font-ui">Ask your agent to fill this</h2>
        <p className="m-0 p-0 text-md text-ink font-ui">
          Give your agent this dataset id, then ask it to call <code>submit_snapshot</code> with the
          rows and then <code>refresh_report</code> for report <code>{detail.report.id}</code>.
        </p>
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm text-ink" data-testid="dataset-id">
            {detail.dataset.id}
          </code>
          <CopyButton value={detail.dataset.id} label="Copy" />
        </div>
        </Stack>
      </Card>

      {uploading ? (
        <CsvUploadDialog
          target={{ dataset: detail.dataset, reportId: detail.report.id }}
          upgradeUrl={props.upgradeUrl}
          onClose={() => setUploading(false)}
          onCreated={() => {
            setUploading(false);
            props.onUploaded();
          }}
        />
      ) : null}
    </section>
  );
}
